/**
 * What the employee directory carries, mirrored from the server.
 *
 * GET /employee answers differently depending on who asks: an admin receives
 * the working record (~43 fields), an employee receives ten. That asymmetry is
 * why two bugs sat here unnoticed — the onboarding gate compared a field the
 * directory does not carry, and the profile wizard prefilled from one. Both
 * worked perfectly for whoever was testing as an admin.
 *
 * So anything reading "a person" out of the directory is narrowed to this
 * shape, for every role. A field that an employee will not have is then absent
 * for the developer too, and the bug shows up on the first run instead of after
 * release. Needing more than this is the signal to fetch the record itself —
 * useSelfEmployee() for yourself, fetchEmployeeDetailsById() for someone else.
 *
 * Mirrored, not imported — the server is a separate repo. Narrowing is the safe
 * direction to drift: if the server adds a directory field, this helper simply
 * does not expose it until it is added here, which is a missing value rather
 * than a leak. Kept in step with DIRECTORY_FIELDS in the API's
 * src/utils/employeeProjection.js.
 */
export const DIRECTORY_FIELDS = [
  "_id",
  "fname",
  "mname",
  "lname",
  "aliasId",
  "designation",
  "department",
  "profileImage",
  "employmentType",
  "isActive",
  "reportingTo",
];

/** A person as a colleague sees them — nothing more, whoever is asking. */
export const toDirectoryShape = (employee) => {
  if (!employee) return employee;
  const out = {};
  DIRECTORY_FIELDS.forEach((f) => {
    if (employee[f] !== undefined) out[f] = employee[f];
  });
  return out;
};
